// Pembagian barang Ready Pre-Order secara FIFO — cermin TS (pure) dari mesin
// SQL `tag_ready_items_and_check_completion` (definisi hidup mig 186, disempurnakan
// mig 4750 — bukan mig 167 seperti tertulis sebelumnya).
//
// SUMBER KEBENARAN tetap SQL (dipanggil server saat "Tandai Ready"); helper ini
// dipakai untuk (a) PRATINJAU di modal Tandai Ready — admin tahu siapa yang
// kebagian sebelum konfirmasi — dan (b) regresi unit (algoritma terdokumentasi).
// Aturan harus IDENTIK dengan SQL: urut waktu order dibuat, bagikan per-pcs,
// item terdepan yang kebagian sebagian dipecah (porsi ready + sisa belum ready).

/** Satu item Belum Ready (keep_status NULL) kandidat penerima stok yang datang. */
export type ReadyCandidate = {
  itemId: string;
  qty: number;
  buyerName: string;
  /** orders.created_at (ISO) — kunci FIFO utama ("pemesan duluan"). */
  orderCreatedAt: string;
  /** order_items.created_at (ISO) — tiebreak ke-2 (cermin SQL). Opsional. */
  itemCreatedAt?: string;
};

/** Satu baris hasil alokasi: berapa pcs item ini jadi Ready. */
export type ReadyAllocationEntry = {
  itemId: string;
  buyerName: string;
  /** pcs yang jadi Ready untuk item ini. */
  takeQty: number;
  /** qty item sebelum dibagi. */
  originalQty: number;
  /** true bila hanya sebagian (takeQty < originalQty) → baris dipecah. */
  isSplit: boolean;
};

export type ReadyAllocationPlan = {
  /** Item yang kebagian (takeQty > 0), urut FIFO. */
  entries: ReadyAllocationEntry[];
  /** Σ takeQty — total pcs yang jadi Ready. */
  allocatedQty: number;
  /** Sisa stok yang tidak terpakai (stok > kebutuhan). */
  leftoverQty: number;
  /** Σ pcs item yang tetap Belum Ready setelah alokasi. */
  unreadyQty: number;
};

const toInt = (n: number): number => (Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0);

/**
 * Bandingkan dua string apa adanya, unit-kode demi unit-kode — BUKAN
 * `localeCompare`.
 *
 * `localeCompare` memakai kolasi ICU, yang memberi bobot khusus pada tanda
 * baca, dan untuk ISO-8601 ber-presisi pecahan detik BERBEDA hasilnya terbalik:
 * `"2026-01-01T03:00:00+00:00".localeCompare("2026-01-01T03:00:00.5+00:00")`
 * memulangkan 1 — mengklaim yang lebih awal justru lebih besar. Postgres
 * memangkas nol di ekor pecahan detik, jadi baris ber-mikrodetik tepat nol
 * dirender tanpa bagian pecahan sama sekali sementara tetangganya membawanya:
 * pasangan itulah yang salah urut. Perbandingan unit-kode benar untuk seluruh
 * bentuk ISO-8601 ber-offset sama, dan untuk UUID huruf-kecil ia setara persis
 * dengan urutan byte `uuid` Postgres.
 */
const compareRaw = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/**
 * Urutkan kandidat FIFO: waktu order dibuat → waktu item dibuat → id (cermin
 * `ORDER BY o.created_at, oi.created_at, oi.id` di SQL). Tidak memutasi input.
 */
export function sortReadyCandidates(candidates: ReadyCandidate[]): ReadyCandidate[] {
  return [...candidates].sort(
    (a, b) =>
      compareRaw(a.orderCreatedAt, b.orderCreatedAt) ||
      compareRaw(a.itemCreatedAt ?? "", b.itemCreatedAt ?? "") ||
      compareRaw(a.itemId, b.itemId)
  );
}

/**
 * Bagikan `toTag` pcs ke kandidat Belum Ready secara FIFO per-pcs. Item terdepan
 * yang kebagian sebagian ditandai `isSplit`. Cermin loop di
 * `tag_ready_items_and_check_completion` (mig 4750).
 */
export function planReadyAllocation(
  candidates: ReadyCandidate[],
  toTag: number
): ReadyAllocationPlan {
  let remaining = toInt(toTag);
  const entries: ReadyAllocationEntry[] = [];
  let unreadyQty = 0;

  for (const c of sortReadyCandidates(candidates)) {
    const qty = toInt(c.qty);
    if (qty <= 0) continue;

    const take = Math.min(remaining, qty);
    if (take > 0) {
      entries.push({
        itemId: c.itemId,
        buyerName: c.buyerName,
        takeQty: take,
        originalQty: qty,
        isSplit: take < qty
      });
      remaining -= take;
    }
    unreadyQty += qty - take;
  }

  const allocatedQty = entries.reduce((sum, e) => sum + e.takeQty, 0);
  return { entries, allocatedQty, leftoverQty: remaining, unreadyQty };
}

/**
 * Berapa pcs BARU yang akan jadi Ready bila admin memasukkan `enteredQty` pcs
 * kedatangan. Cermin SQL: new_arrived = min(qty_arrived + entered, target);
 * to_tag = max(0, new_arrived − sudah_ready). `qtyOrderedEffective` = qty_ordered
 * (fase B) atau qty_active (fase A, sebelum target terisi); `alreadyReady` =
 * qtyReadyTagged + qtyShipped (pcs yang sudah menyerap kedatangan).
 */
export function computeReadyToTag(input: {
  qtyArrived: number;
  qtyOrderedEffective: number;
  alreadyReady: number;
  enteredQty: number;
}): number {
  const newArrived = Math.min(
    toInt(input.qtyArrived) + toInt(input.enteredQty),
    toInt(input.qtyOrderedEffective)
  );
  return Math.max(0, newArrived - toInt(input.alreadyReady));
}
